import type { AccountInfo } from "@azure/msal-browser";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isLocalAuthBypassEnabled } from "../auth/authMode";
import { apiScopes } from "../auth/msalConfig";
import { ensureMsalReady, msalClient } from "../auth/msalClient";
import { AuthContext } from "./useAuth";

/**
 * The sign-in, done in the browser.
 *
 * It replaced App Service Easy Auth, which fetched the token server-side and handed it out
 * through `/.auth/me`. Static Web Apps has no token store, so the browser acquires the token
 * itself - same registration, same scope, same audience the function apps accept.
 *
 * `getAccessToken` keeps its signature on purpose. Everything downstream - `functionApi.ts`
 * and every feature - was written against it and must not need touching.
 */
interface Session {
  isAuthenticated: boolean;
  account: AccountInfo | null;
}

const ANONYMOUS: Session = { isAuthenticated: false, account: null };

/** Only what the rest of the app ever read: a name to show, nothing to decide on. */
function toAccountSummary(account: AccountInfo | null) {
  if (!account) return null;

  return {
    userId: account.homeAccountId ?? null,
    userDetails: account.username ?? null,
    // Entra roles are deliberately empty: no decision in this application hangs on them.
    // The role that matters comes from `me`, out of the database.
    userRoles: [],
    identityProvider: "aad",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(!isLocalAuthBypassEnabled);
  const [session, setSession] = useState<Session>(ANONYMOUS);

  useEffect(() => {
    if (isLocalAuthBypassEnabled) return;

    let mounted = true;

    void (async () => {
      try {
        await ensureMsalReady();

        // Nothing but the local cache is read here, and nothing goes over the network.
        //
        // ssoSilent used to run as a fallback for a tab without a cached account, and it cost
        // every single visitor ten seconds. msal-browser waits `DEFAULT_IFRAME_TIMEOUT_MS`, which
        // is 10_000, for its hidden iframe to report back - and it never reported back: measured
        // in a normal window with a live Microsoft session just as in a private one. Behind that
        // timeout the page showed one line, "Anmeldung wird geprueft", and then declared the
        // visitor signed out anyway. Signing in is one click instead, and that click passes
        // through Entra without a prompt whenever a session exists there.
        const account = msalClient.getAllAccounts()[0] ?? null;

        if (!mounted) return;

        if (account) msalClient.setActiveAccount(account);
        setSession(account ? { isAuthenticated: true, account } : ANONYMOUS);
      } catch (error) {
        // initialize()/handleRedirectPromise() failing (a bad redirect response, corrupted
        // sessionStorage) must not leave the app on the spinner forever. Land on the same
        // signed-out, actionable state a visitor without a session gets, so there is always a
        // sign-in prompt to fall back to.
        console.error("MSAL-Initialisierung fehlgeschlagen, Sitzung gilt als abgemeldet:", error);
        if (!mounted) return;
        setSession(ANONYMOUS);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async () => {
    await ensureMsalReady();
    await msalClient.loginRedirect({ scopes: apiScopes });
  }, []);

  const logout = useCallback(async () => {
    await ensureMsalReady();
    await msalClient.logoutRedirect({ account: session.account ?? undefined });
  }, [session.account]);

  const getAccessToken = useCallback(
    async (scopes?: string[]) => {
      if (!session.account) return null;

      await ensureMsalReady();

      try {
        const result = await msalClient.acquireTokenSilent({
          scopes: scopes ?? apiScopes,
          account: session.account,
        });

        return result.accessToken ?? null;
      } catch (error) {
        // A silent failure is the expensive one: every call would then go without a bearer
        // token and come back as 401, looking like a broken backend. Send them to sign in.
        if (error instanceof InteractionRequiredAuthError || isInteractionRequired(error)) {
          await msalClient.acquireTokenRedirect({ scopes: scopes ?? apiScopes });
        }

        return null;
      }
    },
    [session.account],
  );

  const value = useMemo(
    () => ({
      isAuthenticated: session.isAuthenticated,
      loading,
      isAuthBypassed: false,
      login,
      logout,
      getAccessToken,
      account: toAccountSummary(session.account),
    }),
    [getAccessToken, loading, login, logout, session.account, session.isAuthenticated],
  );

  if (isLocalAuthBypassEnabled) {
    return (
      <AuthContext.Provider
        value={{
          isAuthenticated: true,
          loading: false,
          isAuthBypassed: true,
          login: async () => {},
          logout: async () => {},
          getAccessToken: async () => null,
          account: null,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** MSAL's error class does not survive every bundling path; the error code always does. */
function isInteractionRequired(error: unknown): boolean {
  const code = (error as { errorCode?: string } | null)?.errorCode;

  return code === "interaction_required" || code === "login_required" || code === "consent_required";
}
